import { useEffect, useState } from "react";
import { FaSnowflake, FaUser, FaThermometerHalf, FaArrowUp, FaArrowDown, FaBolt, FaEdit, FaTrash, FaWarehouse } from "react-icons/fa";
import { getSeuils, createSeuil, updateSeuil, deleteSeuil } from "../../services/seuils";
import { getAllEntrepots } from "../../services/entrepots";
import { getAllChambresFroides } from "../../services/chambresfroides";
import { getAllGroupesFrigorifiques } from "../../services/groupesfrigorifiques";
import type { Seuil } from "../../types/seuil";
import type { Entrepot } from "../../types/entrepot";
import type { ChambreFroide } from "../../types/chambrefroide";
import type { GroupeFrigorifique } from "../../types/groupefrigorifique";
import "./Seuils.css";

export default function Seuils() {
  const [seuils, setSeuils] = useState<Seuil[]>([]);
  const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
  const [chambres, setChambres] = useState<ChambreFroide[]>([]);
  const [groupes, setGroupes] = useState<GroupeFrigorifique[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSeuil, setSelectedSeuil] = useState<Seuil | null>(null);
  const [selectedEntrepot, setSelectedEntrepot] = useState<string>("");
  const [selectedChambre, setSelectedChambre] = useState<string>("");
  const [selectedGroupe, setSelectedGroupe] = useState<string>("");


  useEffect(() => {
    const fetchData = async () => {
      const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
        if (Array.isArray(data)) setter(data as T[]);
        else setter([]);
      };

      const seuilsData = await getSeuils();
      const entrepotsData = await getAllEntrepots();
      const chambresData = await getAllChambresFroides();
      const groupesData = await getAllGroupesFrigorifiques();

      safeSetArray<Seuil>(setSeuils)(seuilsData);
      safeSetArray<Entrepot>(setEntrepots)(entrepotsData);
      safeSetArray<ChambreFroide>(setChambres)(chambresData);
      safeSetArray<GroupeFrigorifique>(setGroupes)(groupesData);
    };
    fetchData();
  }, []);

  const groupedByEntrepot = seuils.reduce((acc: Record<string, Seuil[]>, s) => {
    const key = s.entrepot.nom;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const handleDelete = async (id: string) => {
    if (window.confirm("Supprimer ce seuil ?")) {
      await deleteSeuil(id);
      setSeuils(seuils.filter(s => s.id !== id));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const payload = {
        entrepot_id: selectedEntrepot,
        chambre_froide_id: selectedChambre,
        groupe_frigorifique_id: selectedGroupe,
        temperature_min: parseFloat(formData.get("temperature_min") as string),
        temperature_max: parseFloat(formData.get("temperature_max") as string),
        pression_haute_min: parseFloat(formData.get("pression_haute_min") as string),
        pression_haute_max: parseFloat(formData.get("pression_haute_max") as string),
        pression_basse_min: parseFloat(formData.get("pression_basse_min") as string),
        pression_basse_max: parseFloat(formData.get("pression_basse_max") as string),
        courant_a1_min: parseFloat(formData.get("courant_a1_min") as string),
        courant_a1_max: parseFloat(formData.get("courant_a1_max") as string),
        courant_a2_min: parseFloat(formData.get("courant_a2_min") as string),
        courant_a2_max: parseFloat(formData.get("courant_a2_max") as string),
        courant_a3_min: parseFloat(formData.get("courant_a3_min") as string),
        courant_a3_max: parseFloat(formData.get("courant_a3_max") as string),
    };

    if (selectedSeuil) {
        await updateSeuil(selectedSeuil.id, payload);
    } else {
        await createSeuil(payload);
    }

    const data = await getSeuils();
    setSeuils(data);

    // ✅ reset states
    setShowModal(false);
    setSelectedSeuil(null);
    setSelectedEntrepot("");
    setSelectedChambre("");
    setSelectedGroupe("");
    };



  return (
    <div className="seuils-container">
      <div className="seuils-header">
        <button className="btn-ajout" onClick={() => setShowModal(true)}>➕ Ajouter seuil</button>
      </div>

      {Object.entries(groupedByEntrepot).map(([entrepotNom, seuilsEntrepot]) => (
        <div key={entrepotNom} className="entrepot-section">
            <h2 className="entrepot-title">
            <FaWarehouse className="icon-entrepot" /> {entrepotNom}
            </h2>

            {seuilsEntrepot.map((s) => (
            <div key={s.id} className="seuil-card">
                {/* Nom chambre + groupe */}
                <h3 className="badge-seuil">
                <FaSnowflake className="icon-badge" /> {s.chambre_froide?.nom ?? "Chambre inconnue"} - {s.groupe_frigorifique?.nom ?? "Groupe inconnu"}
                </h3>

                {/* Liste des paramètres */}
                <div className="seuil-params">
                <div className="param-line">
                    <FaThermometerHalf className="icon-param" />
                    <span className="param-name">Température :</span>
                    <span className="param-min">{s.temperature_min}</span>
                    <span className="param-separator">--</span>
                    <span className="param-max">{s.temperature_max} °C</span>
                </div>

                <div className="param-line">
                    <FaArrowUp className="icon-param" />
                    <span className="param-name">HP :</span>
                    <span className="param-min">{s.pression_haute_min}</span>
                    <span className="param-separator">--</span>
                    <span className="param-max">{s.pression_haute_max} bar</span>
                </div>

                <div className="param-line">
                    <FaArrowDown className="icon-param" />
                    <span className="param-name">BP :</span>
                    <span className="param-min">{s.pression_basse_min}</span>
                    <span className="param-separator">--</span>
                    <span className="param-max">{s.pression_basse_max} bar</span>
                </div>

                <div className="param-line">
                    <FaBolt className="icon-param" />
                    <span className="param-name">A1 :</span>
                    <span className="param-min">{s.courant_a1_min}</span>
                    <span className="param-separator">--</span>
                    <span className="param-max">{s.courant_a1_max} A</span>
                </div>

                <div className="param-line">
                    <FaBolt className="icon-param" />
                    <span className="param-name">A2 :</span>
                    <span className="param-min">{s.courant_a2_min}</span>
                    <span className="param-separator">--</span>
                    <span className="param-max">{s.courant_a2_max} A</span>
                </div>

                <div className="param-line">
                    <FaBolt className="icon-param" />
                    <span className="param-name">A3 :</span>
                    <span className="param-min">{s.courant_a3_min}</span>
                    <span className="param-separator">--</span>
                    <span className="param-max">{s.courant_a3_max} A</span>
                </div>
                </div>

                {/* Modifié par + boutons */}
                <div className="seuil-footer">
                <p className="modifier-info">
                    <FaUser className="icon-user" /> 
                    Modifié par {s.utilisateur?.first_name} {s.utilisateur?.last_name} le {new Date(s.date_modification).toLocaleDateString()}
                </p>
                <div className="seuil-actions">
                    <FaEdit className="icon-edit" onClick={() => { 
                        setSelectedSeuil(s);
                        setSelectedEntrepot(s.entrepot?.id ?? "");
                        setSelectedChambre(s.chambre_froide?.id ?? "");
                        setSelectedGroupe(s.groupe_frigorifique?.id ?? "");
                        setShowModal(true); }} 
                    />
                    <FaTrash className="icon-trash" onClick={() => handleDelete(s.id)} />
                </div>
                </div>
            </div>
            ))}
        </div>
        ))}



        {showModal && (
        <div className="modal-overlay">
            <div className="modal">
            <h3>{selectedSeuil ? "Modifier seuil" : "Ajouter seuil"}</h3>
            <form onSubmit={handleSubmit}>
                {/* Entrepôt */}
                <select
                name="entrepot"
                value={selectedEntrepot}
                onChange={(e) => setSelectedEntrepot(e.target.value)}
                required
                >
                <option value="">-- Choisir un entrepôt --</option>
                {entrepots.map((e) => (
                    <option key={e.id} value={e.id}>
                    {e.nom}
                    </option>
                ))}
                </select>

                {/* Chambre froide */}
                <select
                name="chambre"
                value={selectedChambre}
                onChange={(e) => setSelectedChambre(e.target.value)}
                required
                >
                <option value="">-- Choisir une chambre froide --</option>
                {chambres
                    .filter((c) => c.entrepot?.id === selectedEntrepot)
                    .map((c) => (
                    <option key={c.id} value={c.id}>
                        {c.nom}
                    </option>
                    ))}
                </select>

                {/* Groupe frigorifique */}
                <select
                name="groupe"
                value={selectedGroupe}
                onChange={(e) => setSelectedGroupe(e.target.value)}
                required
                >
                <option value="">-- Choisir un groupe frigorifique --</option>
                {selectedChambre &&
                    groupes
                    .filter((g) => g.chambre_froide?.id === selectedChambre)
                    .map((g) => (
                        <option key={g.id} value={g.id}>
                        {g.nom}
                        </option>
                    ))}
                </select>

                {/* Champs numériques */}
                <input
                type="number"
                name="temperature_min"
                placeholder="Température min"
                defaultValue={selectedSeuil?.temperature_min ?? ""}
                required
                />
                <input
                type="number"
                name="temperature_max"
                placeholder="Température max"
                defaultValue={selectedSeuil?.temperature_max ?? ""}
                required
                />
                <input
                type="number"
                name="pression_haute_min"
                placeholder="HP min"
                defaultValue={selectedSeuil?.pression_haute_min ?? ""}
                />
                <input
                type="number"
                name="pression_haute_max"
                placeholder="HP max"
                defaultValue={selectedSeuil?.pression_haute_max ?? ""}
                />
                <input
                type="number"
                name="pression_basse_min"
                placeholder="BP min"
                defaultValue={selectedSeuil?.pression_basse_min ?? ""}
                />
                <input
                type="number"
                name="pression_basse_max"
                placeholder="BP max"
                defaultValue={selectedSeuil?.pression_basse_max ?? ""}
                />
                <input
                type="number"
                name="courant_a1_min"
                placeholder="A1 min"
                defaultValue={selectedSeuil?.courant_a1_min ?? ""}
                />
                <input
                type="number"
                name="courant_a1_max"
                placeholder="A1 max"
                defaultValue={selectedSeuil?.courant_a1_max ?? ""}
                />
                <input
                type="number"
                name="courant_a2_min"
                placeholder="A2 min"
                defaultValue={selectedSeuil?.courant_a2_min ?? ""}
                />
                <input
                type="number"
                name="courant_a2_max"
                placeholder="A2 max"
                defaultValue={selectedSeuil?.courant_a2_max ?? ""}
                />
                <input
                type="number"
                name="courant_a3_min"
                placeholder="A3 min"
                defaultValue={selectedSeuil?.courant_a3_min ?? ""}
                />
                <input
                type="number"
                name="courant_a3_max"
                placeholder="A3 max"
                defaultValue={selectedSeuil?.courant_a3_max ?? ""}
                />

                <div className="modal-actions">
                <button type="submit">Enregistrer</button>
                <button type="button" onClick={() => setShowModal(false)}>
                    Annuler
                </button>
                </div>
            </form>
            </div>
        </div>
        )}
    </div>
  );
}
